import React, { useState } from 'react';
import { Card, Form, Button, Row, Col, Spin, Table } from 'antd';
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage';
import BaseListing from 'src/BaseListings';
import { wtApiCall } from 'src/helpers/commons/ApiWrapper';
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications';
import { wtFormatRoundedValue } from 'src/helpers/commons/utils';

const ProjectCashFlow = () => {
    const Base = new BaseListing('project_cash_flow')
    const [isLoading, setIsLoading] = useState(false)
    const [form] = Form.useForm();

    const [projectCashFlowData, setProjectCashFlowData] = useState([])

    const companyName = wtGetFromLocalStorage("company_name");


    // Helper to transform data into the required structure
    const transformData = (data) => {
        const transformedRows = [];
        const months = data.map((item) => `${item.month}-${item.year}`);

        // Define columns: "Description", followed by "Month-Year" and "Month-Year Actual"
        const columns = [...months.flatMap((month) => [month + "<br/>(Target)", month])];
        // Helper function to convert amount to millions
        const toMillion = (value) => (value ? (value / 1_000_000).toFixed(2) : "0.00");


        // Opening Balance
        transformedRows.push({
            key: "opening_balance",
            description: "Opening Balance",
            ...Object.fromEntries(data.flatMap((item) => [
                [`${item.month}-${item.year}<br/>(Target)`, wtFormatRoundedValue(toMillion(item.opening_balance), "", true, 2)],
                [`${item.month}-${item.year}`, wtFormatRoundedValue(toMillion(item.opening_balance_actual), "", true, 2)]
            ])),
            isHeader: true,
        });

        // Inflows Header
        transformedRows.push({
            key: "inflows_header",
            description: "Inflows",
            ...Object.fromEntries(columns.slice(1).map((col) => [col, ""])), // Empty cells for header
            isHeader: true,
        });

        // Inflows Data
        const inflowProjects = data[0]?.projects?.inflows.map((project) => project.project_name) || [];
        inflowProjects.forEach((project) => {
            const row = {
                key: `inflow_${project}`,
                description: project,
                ...Object.fromEntries(
                    data.flatMap((item) => {
                        const inflow = item.projects.inflows.find((p) => p.project_name === project);
                        return [
                            [`${item.month}-${item.year}<br/>(Target)`, inflow ? wtFormatRoundedValue(toMillion(inflow.amount), "", true, 2) : 0],
                            [`${item.month}-${item.year}`, inflow ? wtFormatRoundedValue(toMillion(inflow.actual_amount), "", true, 2) : 0] // Assuming actual_amount key exists
                        ];
                    })
                ),
            };
            transformedRows.push(row);
        });

        // Total Inflows
        transformedRows.push({
            key: "total_inflows",
            description: "Total Inflows",
            ...Object.fromEntries(data.flatMap((item) => [
                [`${item.month}-${item.year}<br/>(Target)`, wtFormatRoundedValue(toMillion(item.total_inflows), "", true, 2)],
                [`${item.month}-${item.year}`, wtFormatRoundedValue(toMillion(item.total_inflows_actual), "", true, 2)]
            ])),
            isHeader: true,
        });

        // Outflows Header
        transformedRows.push({
            key: "outflows_header",
            description: "Outflows",
            ...Object.fromEntries(columns.slice(1).map((col) => [col, ""])), // Empty cells for header
            isHeader: true,
        });

        // Outflows Data
        const outflowProjects = data[0]?.projects?.outflows.map((project) => project.project_name) || [];
        outflowProjects.forEach((project) => {
            const row = {
                key: `outflow_${project}`,
                description: project,
                ...Object.fromEntries(
                    data.flatMap((item) => {
                        const outflow = item.projects.outflows.find((p) => p.project_name === project);
                        return [
                            [`${item.month}-${item.year}<br/>(Target)`, outflow ? wtFormatRoundedValue(toMillion(outflow.amount), "", true, 2) : 0],
                            [`${item.month}-${item.year}`, outflow ? wtFormatRoundedValue(toMillion(outflow.actual_amount), "", true, 2) : 0] // Assuming actual_amount key exists
                        ];
                    })
                ),
            };
            transformedRows.push(row);
        });

        // Total Outflows
        transformedRows.push({
            key: "total_outflows",
            description: "Total Outflows",
            ...Object.fromEntries(data.flatMap((item) => [
                [`${item.month}-${item.year}<br/>(Target)`, wtFormatRoundedValue(toMillion(item.total_outflows), "", true, 2)],
                [`${item.month}-${item.year}`, wtFormatRoundedValue(toMillion(item.total_outflows_actual), "", true, 2)]
            ])),
            isHeader: true,
        });

         // Closing Balance
         transformedRows.push({
            key: "closing_balance",
            description: "Closing Balance",
            ...Object.fromEntries(data.flatMap((item) => [
                [`${item.month}-${item.year}<br/>(Target)`, wtFormatRoundedValue(toMillion(item.closing_balance), "", true, 2)],
                [`${item.month}-${item.year}`, wtFormatRoundedValue(toMillion(item.closing_balance_actual), "", true, 2)]
            ])),
            isHeader: true,
        });

        return { transformedRows, columns };
    };


    const getReportData = () => {

        setIsLoading(true);
        wtApiCall(
            'reports/project_cash_flow_report',
            {
            },
            'get',
            (response) => {

                //set data
                if (response !== null) {
                    if (response.data !== null) {
                        //Set initial values
                        setProjectCashFlowData(response.data);
                    }
                }

                setIsLoading(false);
            },
            (error) => {
                lzNotifyErrorMessageFromResult(error);
                setIsLoading(false);
            },
        );

    };

    // const exportToExcel = () => {
    //     setIsLoading(true);
    //     wtApiPostWithContentTypeHandling('reports/sales_target_report/export', { data: projectCashFlowData });
    //     setIsLoading(false);
    // }


    const { transformedRows, columns } = transformData(projectCashFlowData);

    const tableColumns = [
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            fixed: "left",
            width: 200,
            ellipsis: true,
            render: (text, record) =>
                record.isHeader ? (
                    <strong style={{ fontWeight: "bold", fontSize: 12 }}>{text}</strong>
                ) : (
                    <span style={{ fontSize: 12 }}>{text}</span>
                ),
        },
        ...columns.map((month) => ({
            title: <span dangerouslySetInnerHTML={{ __html: month }} />,
            ellipsis: true,
            dataIndex: month,
            key: month,
            align: "right",
            width: 100,
            render: (text, record) => record.isHeader ? (
                <strong style={{ fontWeight: "bold", fontSize: 12 }}>{text}</strong>
            ) : (
                <span style={{ fontSize: 12 }}>{text}</span>
            ),
        })),
    ];

    return (
        <div className='container-fluid' style={{
            padding: "1em", backgroundColor: "white", minHeight: "100vh", fontFamily: "Helvetica",
        }}>
            <Form
                layout={'vertical'}
                form={form}
                onFinish={getReportData}
                style={{ maxWidth: "100%", margin: '0 auto' }}
            >
                <Card title={<span style={{ fontSize: '22px', marginLeft: "40%" }}>{companyName + " Project Cash Flow"}</span>} style={{ width: "100%" }}>

                    <Row gutter={[24, 24]}>

                        <Col xs={12} sm={8} md={5}>
                            <Button style={{ marginTop: 30, marginRight: 5 }} type="primary" htmlType="submit" disabled={isLoading ? true : false}>Search</Button>
                            {/* <Button style={{ marginTop: 30, backgroundColor: "#4BB543", color: "#fff" }} onClick={exportToExcel} disabled={isLoading ? true : false}>Export</Button> */}
                            {/* <Button style={{ marginTop: 30, backgroundColor: "red", color: "#fff" }} onClick={print} disabled={isLoading ? true : false}>Print</Button> */}

                        </Col>
                    </Row>

                    <br />
                    {(projectCashFlowData.length > 0) &&
                        <div style={{ width: "100%" }}>
                            <Table
                                columns={tableColumns}
                                dataSource={transformedRows}
                                pagination={false}
                                bordered
                                size="small"
                                scroll={{ x: "max-content", y: 420 }}
                                virtual={true}
                                loading={{ indicator: <div><Spin /></div>, spinning: isLoading }}
                            />
                        </div>
                    }
                </Card>
            </Form>
        </div >
    );
};

export default ProjectCashFlow;
